import React, { useMemo, useState } from "react";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { Localizer } from "@/main/react/Types";
import SimpleDialog, { openSimpleDialog, SimpleDialogFooter } from "@/main/react/Dialog/SimpleDialog";
import { getSafeToastMessageFromError } from "@/main/common/ApiUtils";
import { toast } from "sonner";
import logger from "@/lib/logger";
import type { GroupInvitationAccessLevel } from "@/main/common/GroupGovernanceApi";
import { UsernameSuggestionField } from "@/main/react/component/UsernameSuggestionField";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import { ShieldPlus, UserPlus2, X } from "lucide-react";

const ACCESS_LEVEL_OPTIONS: Array<{ value: GroupInvitationAccessLevel; labelKey: LocalizationKey }> = [
    { value: "ADMINISTRATOR", labelKey: "group.leadership.administrator" },
    { value: "MODERATOR", labelKey: "group.leadership.moderator" },
    { value: "MEMBER", labelKey: "group.leadership.member" },
];

type GroupLeadershipActionDialogOptions = {
    localizer: Localizer;
    title: string;
    confirmLabel: string;
    usernameLabel?: string;
    usernamePlaceholder?: string;
    initialUsername?: string;
    initialAccessLevel?: GroupInvitationAccessLevel;
    allowedAccessLevels?: GroupInvitationAccessLevel[];
    lockUsername?: boolean;
    onConfirm: (payload: { username: string; accessLevel: GroupInvitationAccessLevel }) => Promise<void> | void;
};

export function GroupLeadershipActionDialogInner({
    localizer,
    title,
    confirmLabel,
    usernameLabel,
    usernamePlaceholder,
    initialUsername = "",
    initialAccessLevel = "MODERATOR",
    allowedAccessLevels,
    lockUsername = false,
    onConfirm,
    onClose,
}: GroupLeadershipActionDialogOptions & { onClose: () => void }) {
    const availableAccessLevels = useMemo(() => {
        const requested = Array.isArray(allowedAccessLevels) && allowedAccessLevels.length > 0 ? allowedAccessLevels : ACCESS_LEVEL_OPTIONS.map((option) => option.value);
        return ACCESS_LEVEL_OPTIONS.filter((option) => requested.includes(option.value));
    }, [allowedAccessLevels]);
    const [username, setUsername] = useState(initialUsername);
    const [accessLevel, setAccessLevel] = useState<GroupInvitationAccessLevel>(
        availableAccessLevels.some((option) => option.value === initialAccessLevel) ? initialAccessLevel : availableAccessLevels[0]?.value ?? "MODERATOR"
    );
    const [busy, setBusy] = useState(false);

    async function handleConfirm() {
        const trimmedUsername = username.trim();
        if (!trimmedUsername || busy) {
            return;
        }

        setBusy(true);
        try {
            await onConfirm({ username: trimmedUsername, accessLevel });
            onClose();
        } catch (error) {
            logger.error("[GroupLeadershipActionDialog] confirm failed", error);
            toast.error(getSafeToastMessageFromError(error, localizer("dialog.confirm.action_failed")));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="space-y-4">
            <div>
                <label className="mb-2 block text-sm font-medium text-text-main">
                    {usernameLabel ?? localizer("group.leadership.username_label")}
                </label>
                <UsernameSuggestionField
                    localizer={localizer}
                    value={username}
                    onChange={setUsername}
                    disabled={busy || lockUsername}
                    readOnly={lockUsername}
                    placeholder={usernamePlaceholder ?? localizer("group.leadership.username_placeholder")}
                    className="w-full rounded-xl border border-border bg-[var(--background1)] px-3 py-2 text-sm text-text-main outline-none transition-colors focus:border-primary/40"
                />
            </div>

            {availableAccessLevels.length > 1 ? (
                <div>
                    <label className="mb-2 block text-sm font-medium text-text-main">
                        {localizer("group.leadership.level_label")}
                    </label>
                    <select
                        value={accessLevel}
                        onChange={(event) => setAccessLevel(event.target.value as GroupInvitationAccessLevel)}
                        disabled={busy}
                        className="w-full rounded-xl border border-border bg-[var(--background1)] px-3 py-2 text-sm [@media(pointer:coarse)]:text-base text-text-main outline-none transition-colors focus:border-primary/40"
                    >
                        {availableAccessLevels.map((option) => (
                            <option key={option.value} value={option.value}>
                                {localizer(option.labelKey)}
                            </option>
                        ))}
                    </select>
                </div>
            ) : null}

            <SimpleDialogFooter>
                <CtaButton
                    type="button"
                    icon={X}
                    text={localizer("literal.cancel")}
                    variant="outline"
                    disabled={busy}
                    onClick={onClose}
                />
                <CtaButton
                    type="button"
                    icon={title === localizer("group.invitations.invite_user") ? UserPlus2 : ShieldPlus}
                    text={confirmLabel}
                    variant="primary"
                    disabled={busy || !username.trim()}
                    onClick={() => void handleConfirm()}
                />
            </SimpleDialogFooter>
        </div>
    );
}

export function showGroupLeadershipActionDialog(options: GroupLeadershipActionDialogOptions) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div>{options.title}</div>}
            width="md"
        >
            <GroupLeadershipActionDialogInner
                {...options}
                onClose={() => onOpenChange(false)}
            />
        </SimpleDialog>
    ));
}
